import {BlockType, Filter, FilterQuery, ValidationMessage, type FilterProvider} from '@github-ui/filter'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'

export const singleValueFilterTypes = {
  exclusive: false,
  valueless: false,
  hasValue: false,
  multiKey: false,
  multiValue: false,
} as const

export type FilterValue = {value?: string | (() => string); displayName?: string}

export type QueryFieldConfig<T> = {
  providerKey: string
  filterKey: keyof T
  getValue?: (values: FilterValue[]) => unknown
}

const convertQueryToFilter = <T extends Record<string, unknown>>(
  request: FilterQuery,
  fieldConfigs: Array<QueryFieldConfig<T>>,
): T => {
  const filter = {} as T

  const filterBlocks = request.blocks.filter(b => b.type === BlockType.Filter)
  for (const b of filterBlocks) {
    const key = b.provider?.key
    const values = b.value?.values

    if (!key || !values || values.length === 0) continue

    const config = fieldConfigs.find(c => c.providerKey === key)
    if (config) {
      const getValue = config.getValue ?? extractValue
      const value = getValue(values)
      if (value !== undefined) {
        ;(filter as Record<string, unknown>)[config.filterKey as string] = value
      }
    }
  }

  return filter
}

const extractValue = (values: FilterValue[]) => {
  const val = values[0]?.value
  return typeof val === 'function' ? val() : val
}
export const extractAsUser = (values: FilterValue[]) => {
  const val = values[0]?.value
  const login = typeof val === 'function' ? val() : val
  return {
    login: login as string,
    name: '',
    path: '',
    primaryAvatarUrl: '',
  }
}

export const timePeriodQueryField = {providerKey: 'time-period', filterKey: 'timePeriod'} as const
export const orgQueryField = {providerKey: 'org', filterKey: 'organization'} as const
export const repositoryQueryField = {providerKey: 'repo', filterKey: 'repository'} as const

export type FilterFieldConfig<T> = {
  key: keyof T
  queryKey: string
  getValue?: (value: T[keyof T]) => string
  quoted?: boolean
}

const convertFilterToQuery = <T extends Record<string, unknown>>(
  filter: T,
  fieldConfigs: Array<FilterFieldConfig<T>>,
): string => {
  const queryParts: string[] = []

  for (const {key, queryKey, getValue = String, quoted} of fieldConfigs) {
    const value = filter[key]
    if (value != null) {
      const stringValue = getValue(value)
      queryParts.push(quoted ? `${queryKey}:"${stringValue}"` : `${queryKey}:${stringValue}`)
    }
  }

  return queryParts.join(' ')
}

export const getLogin = (v: unknown) => (v as {login: string}).login
export const getName = (v: unknown) => (v as {name: string}).name

export const timePeriodField = {key: 'timePeriod', queryKey: 'time-period'} as const
export const repositoryField = {key: 'repository', queryKey: 'repository'} as const
export const organizationField = {key: 'organization', queryKey: 'organization'} as const

type SecurityProductsFilterBarProps<T extends Record<string, unknown> & {page?: number}> = {
  id: string
  label: string
  filter: T
  fieldConfigs: Array<FilterFieldConfig<T>>
  queryFieldConfigs: Array<QueryFieldConfig<T>>
  filterProviders: FilterProvider[]
  getIndexPath: (options: {filter: T}) => string
}

export const SecurityProductsFilterBar = <T extends Record<string, unknown> & {page?: number}>({
  id,
  label,
  filter,
  fieldConfigs,
  queryFieldConfigs,
  filterProviders,
  getIndexPath,
}: SecurityProductsFilterBarProps<T>) => {
  const filterInputRef = useRef<HTMLInputElement | null>(null)
  const {navigate} = useRelativeNavigation()
  const [filterValue, setFilterValue] = useState(() => convertFilterToQuery(filter, fieldConfigs))

  const showFilterDialog = useFeatureFlag('security_products_filter_bar_afd')

  const filterData = useCallback(
    (request: FilterQuery) => {
      const newFilter = convertQueryToFilter(request, queryFieldConfigs)
      newFilter.page = undefined // Reset to first page when filter changes
      navigate('.', getIndexPath({filter: newFilter}), true)
    },
    [navigate, queryFieldConfigs, getIndexPath],
  )

  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    const timeoutId = setTimeout(() => {
      const input = filterInputRef.current
      if (input) {
        input.focus()
        const len = input.value.length
        input.setSelectionRange(len, len)
      }
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [filter])

  const [validationMessage, setValidationMessage] = useState<string[]>([])

  const handleSubmit = useCallback(
    (request: FilterQuery) => {
      const rawText: string[] = []
      for (const block of request.blocks) {
        if (block.type === BlockType.Text) {
          const errorMessage = `Text will be ignored, use one of the predefined filters instead: <pre>${block.raw}</pre>`
          rawText.push(errorMessage)
        }
      }
      if (rawText.length > 0 || request.errors.length > 0) {
        setValidationMessage([...request.errors, ...rawText])
      } else {
        setValidationMessage([])
      }
      filterData(request)
    },
    [filterData],
  )

  return (
    <div>
      <Filter
        id={id}
        label={label}
        onSubmit={handleSubmit}
        providers={filterProviders}
        filterValue={filterValue}
        onChange={setFilterValue}
        variant={showFilterDialog ? 'full' : 'input'}
        inputRef={filterInputRef}
      />
      {validationMessage.length > 0 && (
        <ValidationMessage messages={validationMessage} id={`${id}-validation-message`} />
      )}
    </div>
  )
}

try{ SecurityProductsFilterBar.displayName ||= 'SecurityProductsFilterBar' } catch {}